import { Landmark, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader } from "@/components/common/loader";
import { getEFilingStatus } from "@/features/efiling/api";
import { NewSubmissionDialog } from "@/features/efiling/components/new-submission-dialog";
import { SubmissionStatusBadge } from "@/features/efiling/components/submission-status-badge";
import { useClientSubmissions } from "@/features/efiling/hooks/use-client-submissions";
import type { EFilingStatus } from "@/features/efiling/types";
import { useActiveClient } from "@/layouts/client-layout";

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function ClientEFilingPage() {
  const { client } = useActiveClient();
  const { submissions, loading, error, refetch } = useClientSubmissions(client.id);
  const [status, setStatus] = useState<EFilingStatus | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    getEFilingStatus()
      .then(setStatus)
      .catch(() => setStatus(null));
  }, []);

  const disabledReason = status && !status.efilingEnabled
    ? "IRS e-Filing isn't enabled for your firm yet. A country director can turn it on in Settings › Integrations."
    : status?.efilingDown
      ? status.efilingMessage || "IRS e-Filing is temporarily unavailable."
      : null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-foreground mb-2 flex items-center gap-2 text-3xl font-bold">
            <Landmark className="text-primary size-7" /> IRS e-Filing
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            Select this client's completed forms and documents, choose the IRS
            channel, and submit — attributed to the client's assigned practitioner.
            Transmission is handled by your firm's IRS connector.
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="size-4" /> New submission
        </Button>
      </div>

      {disabledReason && (
        <div className="border-warning/40 bg-warning/10 text-warning rounded-lg border px-4 py-3 text-sm">
          {disabledReason}
        </div>
      )}

      <Card className="gap-0 overflow-hidden p-0">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : error ? (
          <p className="text-destructive p-6 text-sm">{error}</p>
        ) : submissions.length === 0 ? (
          <p className="text-muted-foreground p-6 text-sm">
            No IRS submissions for this client yet. Create one to get started.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Channel</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Practitioner</TableHead>
                <TableHead>Confirmation</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {submissions.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">
                    <Link
                      to={`/client/${client.id}/efiling/${s.id}`}
                      className="text-foreground hover:text-primary hover:underline"
                    >
                      {s.channelDisplay}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {s.items.length}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {s.practitionerName}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {s.irsConfirmationNumber || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDate(s.createdAt)}
                  </TableCell>
                  <TableCell>
                    <SubmissionStatusBadge
                      status={s.status}
                      label={s.statusDisplay}
                      title={s.errorMessage || s.statusDisplay}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <NewSubmissionDialog
        clientId={client.id}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={() => refetch()}
      />
    </div>
  );
}
